import React from 'react';
import { CheckCircle, XCircle, Download, CreditCard } from 'lucide-react';

// Helper function to safely format currency
const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === null || num === undefined) {
    return '0.00';
  }
  return num.toFixed(2);
};

const PaymentSummary = ({ 
  booking, 
  totalPaid, 
  depositPaid, 
  fullPaymentPaid, 
  payments, 
  onDownloadInvoice, 
  onViewPaymentHistory 
}) => {
  // Calculate amounts using new payment structure
  // For vendors: only show service amounts (no platform fee visible)
  const vendorPayout = parseFloat(booking?.vendor_payout_amount || booking?.subtotal_amount || 0);
  const serviceAmountReceived = parseFloat(totalPaid || 0); // This is already calculated as service amount in VendorBookingDetails
  const remainingBalance = Math.max(0, vendorPayout - serviceAmountReceived);
  const isFullyPaid = serviceAmountReceived >= vendorPayout && vendorPayout > 0;

  // Only show deposit status if there are actual deposit payments and booking is not fully paid
  const showDepositStatus = depositPaid > 0 && !isFullyPaid;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
      <h3 className="text-sm sm:text-base font-bold text-black mb-2 sm:mb-3">Payment Summary</h3>
      <div className="space-y-1.5 sm:space-y-2">
        <div className="flex justify-between items-center py-1.5 border-b-2 border-gray-300">
          <span className="text-sm text-black font-bold">Service Amount:</span>
          <span className="text-base sm:text-lg font-bold text-black">${formatCurrency(vendorPayout)}</span>
        </div>
        <div className="flex justify-between items-center pt-1">
          <span className="text-xs text-gray-600 font-medium">Total Service Amount Received:</span>
          <span className="text-sm font-medium text-black">${formatCurrency(totalPaid)}</span>
        </div>
        {showDepositStatus && (
          <div className="flex justify-between items-center pt-1">
            <span className="text-xs text-gray-600 font-medium">Deposit Paid:</span>
            <span className="text-xs font-medium text-black">${formatCurrency(depositPaid)}</span>
          </div>
        )}
        {fullPaymentPaid && (
          <div className="flex justify-between items-center pt-1">
            <span className="text-xs text-gray-600 font-medium">Full Payment:</span>
            <span className="text-xs font-medium text-black flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />
              Paid
            </span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600 font-medium">Payment Status:</span>
          <span className={`text-xs font-medium ${isFullyPaid ? 'text-green-600' : serviceAmountReceived > 0 ? 'text-amber-600' : 'text-gray-600'}`}>
            {isFullyPaid ? (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                Fully Paid
              </span>
            ) : serviceAmountReceived > 0 ? (
              <span className="flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" />
                Partial
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" />
                Pending
              </span>
            )}
          </span>
        </div>
        {remainingBalance > 0 && (
          <div className="flex justify-between items-center pt-1 border-t border-gray-300">
            <span className="text-xs text-gray-600 font-medium">Balance:</span>
            <span className="text-sm font-medium text-black">${formatCurrency(remainingBalance)}</span>
          </div>
        )}
      </div>
      
      {/* Payment Actions */}
      <div className="mt-3 sm:mt-4 pt-3 border-t-2 border-gray-300 space-y-2">
        <button
          onClick={onDownloadInvoice}
          className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-black text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          Download Invoice
        </button>
        {payments.length > 0 && (
          <button
            onClick={onViewPaymentHistory}
            className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-black text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          >
            <CreditCard className="w-4 h-4" />
            Payment History
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentSummary;

